"use client";

const strengths = [
  {
    title: "Comparable scores across 4,000+ stocks",
    text: "Every company is measured on the same 0 to 100 scale across nine strategies.",
  },
  {
    title: "Forward-looking fundamental analysis",
    text: "Valuations use consensus forecasts alongside historical fundamentals, not history alone.",
  },
  {
    title: "Transparent strategy track records",
    text: "Systematic portfolios record every rebalance and trade. Good periods and bad periods stay visible.",
  },
];

const tradeoffs = [
  {
    title: "Not a live trading terminal",
    text: "Ratings use verified end-of-day fundamentals. Bastion is not built for tick-by-tick decisions.",
  },
  {
    title: "Not personal recommendations",
    text: "Bastion organizes financial data and scoring. It does not tell you what to buy or make decisions for you.",
  },
  {
    title: "Not every stock on Earth",
    text: "Reliable scoring needs consistent fundamentals and analyst consensus. Some companies cannot be covered responsibly.",
  },
];

function Column({
  label,
  dotColor,
  items,
}: {
  label: string;
  dotColor: string;
  items: { title: string; text: string }[];
}) {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-2 h-2" style={{ backgroundColor: dotColor }} />
        <span className="text-sm font-medium text-white/60 tracking-wide uppercase">
          {label}
        </span>
      </div>

      <dl>
        {items.map((item) => (
          <div key={item.title} className="py-5 border-b border-white/[0.07] last:border-b-0">
            <dt className="text-[15px] font-semibold text-white/90 mb-1">
              {item.title}
            </dt>
            <dd className="text-sm text-white/55 leading-relaxed">{item.text}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function PhilosophySection() {
  return (
    <section id="philosophy" className="pt-14 pb-24 md:pt-20 md:pb-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-48 pointer-events-none z-10" style={{ background: "linear-gradient(to bottom, rgba(14,14,14,0) 0%, rgba(14,14,14,0.55) 55%, #0E0E0E 100%)" }} />
      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-20">
        <div className="text-center mb-12 md:mb-16">
          <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#DCE2EC]/70">
            Honest by Design
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold mb-5 text-white">
            What Bastion Is, and What It Isn&rsquo;t
          </h2>
          <p className="text-white/55 max-w-2xl mx-auto leading-relaxed">
            A systematic research tool for long-term investors. Comparable
            scores, transparent track records, and clear limits.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <Column label="What Bastion gives you" dotColor="#9FE870" items={strengths} />
          <Column label="What Bastion does not pretend to be" dotColor="#F5D545" items={tradeoffs} />
        </div>

        <p className="mt-12 pt-8 border-t border-white/[0.07] text-center text-base text-white/70 leading-relaxed">
          Bastion does not make the decision for you. It gives you a clearer
          basis for making your own.
        </p>
      </div>
    </section>
  );
}
